import {Empty} from 'antd';
import Form from 'antd/es/form';
import useForm from 'antd/es/form/hooks/useForm';
import cn from 'classnames';
import React, {
    useContext, useEffect, useState, FC,
} from 'react';

import {EDocumentUploadTemplateTabKey} from 'components/document-upload-template/document-upload-template-constants';
import {setFieldsValueUntouched} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {
    selectDocumentUploadTemplateData, selectSettingTemplateTargetColumns,
} from 'store/slices/document-upload-template/document-upload-template-selectors';
import {
    loadDocumentUploadTemplateData, loadSettingTemplateTargetColumns,
    updateDocumentUploadTemplateConfiguration,
} from 'store/slices/document-upload-template/document-upload-template-slice-thunks';
import {
    IDocumentUploadTemplateTypeLookupData,
} from 'store/slices/document-upload-template/document-upload-template-slice-type';

import {EWatcherFieldFlag} from '../../../report-configuration/hooks/use-form-fields-manager';
import {useFormListFieldsManager} from '../../../report-configuration/hooks/use-form-list-fields-manager';
import {
    SettingTemplateTargetTableButtons,
} from '../../components/setting-template-target-table-buttons/setting-template-target-table-buttons';
import {DocumentUploadTemplateContext} from '../../document-upload-template-context';
import {
    SettingTemplateTransferTargetFields,
} from './components/setting-template-transfer-target-filds/setting-template-transfer-target-fields';
import {
    SettingTemplateTransferTargetFilter,
} from './components/setting-template-transfer-target-filter/setting-template-transfer-target-filter';
import {
    SETTING_TEMPLATE_TRANSFER_TARGET_FORM_DATA_KEY,
    ESettingTemplateTransferTargetFormFields,
} from './constants/setting-template-transfer-target/setting-template-transfer-target';
import {
    convertSettingTemplateTransferTargetFieldsData,
} from './setting-template-transfer-target-utils';

interface ISettingTemplateTransferTargetProps{
    entityName: string;
}
export const SettingTemplateTransferTarget: FC<ISettingTemplateTransferTargetProps> = (
    {entityName} : ISettingTemplateTransferTargetProps,
) => {
    const [form] = useForm();
    const [formState, setFormState] = useState(
        {initialValues: {}, isEditingForm: false, isDirty: false},
    );
    const documentUploadTemplate = useAppSelector(selectDocumentUploadTemplateData);
    const lookupData = useAppSelector(selectSettingTemplateTargetColumns) || [];
    const [initColumns, setColumns] = useState<IDocumentUploadTemplateTypeLookupData[] | undefined>();
    const dispatch = useAppDispatch();
    const {templateCode} = useContext(DocumentUploadTemplateContext);
    const disabledByEditingModeCondition = {
        condition: () => !formState.isEditingForm,
        flag: EWatcherFieldFlag.isDisabled,
    };
    const {
        applyFieldWatchers,
        checkWatcherFlag,
        checkArrayIndependentWatcherFlag,
    } = useFormListFieldsManager({
        formInstance: form,
        watchers: {
            [ESettingTemplateTransferTargetFormFields.description]: [
                disabledByEditingModeCondition,
            ],
            [ESettingTemplateTransferTargetFormFields.fieldTypeId]: [
                disabledByEditingModeCondition,
            ],
            [ESettingTemplateTransferTargetFormFields.sourceField]: [
                {
                    condition: ({formValues}) => {
                        const sql = formValues?.[ESettingTemplateTransferTargetFormFields?.sourceFieldDynamicSQL];
                        const constant = formValues?.[ESettingTemplateTransferTargetFormFields?.sourceFieldConstant];
                        return (!formState.isEditingForm)
                            || (sql !== '' && sql)
                            || (constant !== '' && constant);
                    },
                    flag: EWatcherFieldFlag.isDisabled,
                },
            ],
            [ESettingTemplateTransferTargetFormFields.sourceFieldDynamicSQL]: [
                {
                    condition: ({formValues}) => {
                        const source = formValues?.[ESettingTemplateTransferTargetFormFields?.sourceField];
                        const constant = formValues?.[ESettingTemplateTransferTargetFormFields?.sourceFieldConstant];
                        return (!formState.isEditingForm)
                            || (source !== '' && source) || (constant !== '' && constant);
                    },
                    flag: EWatcherFieldFlag.isDisabled,
                },
            ],
            [ESettingTemplateTransferTargetFormFields.sourceFieldConstant]: [
                {
                    condition: ({formValues}) => {
                        const source = formValues?.[ESettingTemplateTransferTargetFormFields?.sourceField];
                        const sql = formValues?.[ESettingTemplateTransferTargetFormFields?.sourceFieldDynamicSQL];
                        return (!formState.isEditingForm)
                            || (source !== '' && source) || (sql !== '' && sql);
                    },
                    flag: EWatcherFieldFlag.isDisabled,
                },
            ],
        },
        arrayIndependentWatchers: {
            [ESettingTemplateTransferTargetFormFields.SOURCE_TABLE]: [
                disabledByEditingModeCondition,
            ],
            [ESettingTemplateTransferTargetFormFields.TARGET_TABLE]: [
                disabledByEditingModeCondition,
            ],
            formatSetting: [
                {
                    condition: () => !formState.isEditingForm
                    || !(
                        documentUploadTemplate?.dffSegmentValues?.[
                            ESettingTemplateTransferTargetFormFields.TARGET_TABLE
                        ]?.segmentValue
                       === form.getFieldValue(
                           ESettingTemplateTransferTargetFormFields.TARGET_TABLE,
                       )),
                    flag: EWatcherFieldFlag.isDisabled,
                },
            ],
        },
        formArrayKey: SETTING_TEMPLATE_TRANSFER_TARGET_FORM_DATA_KEY,
        triggerDeps: [formState.isEditingForm],
        modifiers: {
            trackFormDirtyState: true,
            isDirty: formState.isDirty,
            setIsDirty: isDirty => setFormState(v => ({...v, isDirty})),
        },
    });

    useEffect(() => {
        if (documentUploadTemplate?.dffSegmentValues?.[
            ESettingTemplateTransferTargetFormFields.TARGET_TABLE
        ]?.segmentValue && !initColumns) {
            dispatch(loadSettingTemplateTargetColumns({
                sublistCode: (
                    documentUploadTemplate?.dffSegmentValues?.[
                        ESettingTemplateTransferTargetFormFields.TARGET_TABLE
                    ]?.segmentValue
                ),
            })).then(
                res => setColumns(
                    res?.payload as IDocumentUploadTemplateTypeLookupData[] || [],
                ),
            );
        }
    }, [documentUploadTemplate]);

    useEffect(() => {
        if (lookupData.length) {
            if (documentUploadTemplate?.dffSegmentValues?.[
                ESettingTemplateTransferTargetFormFields.TARGET_TABLE
            ]?.segmentValue === form.getFieldValue(
                ESettingTemplateTransferTargetFormFields.TARGET_TABLE,
            )) {
                form.setFieldsValue({
                    [SETTING_TEMPLATE_TRANSFER_TARGET_FORM_DATA_KEY]: convertSettingTemplateTransferTargetFieldsData(
                        lookupData,
                        documentUploadTemplate?.excelDataMappings,
                    ),
                });
                return;
            }
            form.setFieldsValue({
                [SETTING_TEMPLATE_TRANSFER_TARGET_FORM_DATA_KEY]: convertSettingTemplateTransferTargetFieldsData(
                    lookupData,
                    [],
                ),
            });
        }
        applyFieldWatchers();
    }, [lookupData]);

    useEffect(() => {
        if (initColumns && documentUploadTemplate) {
            const {excelDataMappings} = documentUploadTemplate ?? [];
            const dataToSetToForm = convertSettingTemplateTransferTargetFieldsData(
                initColumns,
                excelDataMappings,
            );
            form.resetFields();
            setFieldsValueUntouched(form, {
                [ESettingTemplateTransferTargetFormFields.SOURCE_TABLE]: documentUploadTemplate?.dffSegmentValues?.[
                    ESettingTemplateTransferTargetFormFields.SOURCE_TABLE
                ]?.segmentValue,
                [ESettingTemplateTransferTargetFormFields.TARGET_TABLE]: documentUploadTemplate?.dffSegmentValues?.[
                    ESettingTemplateTransferTargetFormFields.TARGET_TABLE
                ]?.segmentValue,
                [SETTING_TEMPLATE_TRANSFER_TARGET_FORM_DATA_KEY]: dataToSetToForm,
            });
            setFormState(
                v => (
                    {
                        ...v,
                        initialValues: {
                            [ESettingTemplateTransferTargetFormFields.SOURCE_TABLE]: (
                                documentUploadTemplate?.dffSegmentValues?.[
                                    ESettingTemplateTransferTargetFormFields.SOURCE_TABLE
                                ]?.segmentValue
                            ),
                            [ESettingTemplateTransferTargetFormFields.TARGET_TABLE]: (
                                documentUploadTemplate?.dffSegmentValues?.[
                                    ESettingTemplateTransferTargetFormFields.TARGET_TABLE
                                ]?.segmentValue
                            ),
                            [SETTING_TEMPLATE_TRANSFER_TARGET_FORM_DATA_KEY]: dataToSetToForm,
                        },
                    }
                ),
            );
        }
        applyFieldWatchers();
    }, [documentUploadTemplate, initColumns]);

    const handleFinish = (values: any) => {
        if (templateCode) {
            dispatch(
                updateDocumentUploadTemplateConfiguration({
                    entityName,
                    templateCode,
                    data: {
                        dffSegmentValues: {
                            [ESettingTemplateTransferTargetFormFields.SOURCE_TABLE]: {
                                segmentColumnName: ESettingTemplateTransferTargetFormFields.SOURCE_TABLE,
                                segmentValue: values.SOURCE_TABLE,
                            },
                            [ESettingTemplateTransferTargetFormFields.TARGET_TABLE]: {
                                segmentColumnName: ESettingTemplateTransferTargetFormFields.TARGET_TABLE,
                                segmentValue: values.TARGET_TABLE,
                            },
                        },
                        excelDataMappings: values.excelDataMappings,
                    },
                }),
            ).then(() => {
                dispatch(loadDocumentUploadTemplateData({
                    entityName,
                    templateCode,
                }));
            });
        }
    };

    return (
        <div className={cn('setting-template-transfer-target')} >
            <Form
                layout="vertical"
                form={form}
                name={SETTING_TEMPLATE_TRANSFER_TARGET_FORM_DATA_KEY}
                onValuesChange={changedValues => {
                    applyFieldWatchers(changedValues);
                }}
                onFinish={handleFinish}
                className={cn('setting-template-transfer-target__form')}
            >
                <SettingTemplateTargetTableButtons {...{
                    formState,
                    setFormState,
                    form,
                    tabName: EDocumentUploadTemplateTabKey.SETTING_TEMPLATE_TRANSFER_TARGET,
                }}
                />
                <SettingTemplateTransferTargetFilter
                    form={form}
                    checkArrayIndependentWatcherFlag={checkArrayIndependentWatcherFlag}
                />
                <div className={cn('setting-template-target__body')}>
                    <Form.List name={SETTING_TEMPLATE_TRANSFER_TARGET_FORM_DATA_KEY}>{fields => (
                        <>
                            {!fields.length && <Empty />}
                            <SettingTemplateTransferTargetFields
                                form={form}
                                formState={formState}
                                fields={fields}
                                checkWatcherFlag={checkWatcherFlag}
                                documentUploadTemplate={documentUploadTemplate}
                            />
                        </>
                    )}
                    </Form.List>
                </div>
            </Form>
        </div>
    );
};
